import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { AddWishListItemRequestViewModel } from '../models/AddWishListItemRequestViewModel';
import type { AddWishListItemViewModel } from '../models/AddWishListItemViewModel';
import type { CreateWishListViewModel } from '../models/CreateWishListViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { WishListItemsPaginatedViewModel } from '../models/WishListItemsPaginatedViewModel';
import type { WishListUpdateRequestViewModel } from '../models/WishListUpdateRequestViewModel';
import type { WishListViewModel } from '../models/WishListViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class WishListsService {
  /**
   * Create (WishLists)
   * Creates new wishlist
   * @returns CreateWishListViewModel Success
   * @throws ApiError
   */
  public static async createWishList(
    {
      authorization,
      body,
    }: {
      authorization?: string;
      body?: CreateWishListViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CreateWishListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<CreateWishListViewModel>({
      method: 'POST',
      url: `/v2/wishlists`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (WishLists)
   * Gets wishlists by user
   * @returns WishListViewModel Success
   * @throws ApiError
   */
  public static async getWishLists(
    {
      authorization,
    }: {
      authorization?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<WishListViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<WishListViewModel>>({
      method: 'GET',
      url: `/v2/wishlists`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Delete (WishLists)
   * Deletes wish lists by id
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async deleteWishList(
    {
      id,
    }: {
      id: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/wishlists/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update (WishLists)
   * Updates wishlist by id
   * @returns WishListViewModel Success
   * @throws ApiError
   */
  public static async updateWishList(
    {
      id,
      body,
    }: {
      id: string;
      body?: WishListUpdateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WishListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WishListViewModel>({
      method: 'PUT',
      url: `/v2/wishlists/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (WishLists)
   * Gets wishlist by id
   * @returns WishListViewModel Success
   * @throws ApiError
   */
  public static async getWishListById(
    {
      id,
    }: {
      id: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WishListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WishListViewModel>({
      method: 'GET',
      url: `/v2/wishlists/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Selected (WishLists)
   * Gets selected wishlists
   * @returns WishListViewModel Success
   * @throws ApiError
   */
  public static async getSelectedWishLists(
    {
      itemId,
      type,
      authorization,
    }: {
      itemId: number;
      type: any;
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<WishListViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<WishListViewModel>>({
      method: 'GET',
      url: `/v2/wishlists/selected`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Selected (WishLists)
   * Deletes selected wish lists
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async deleteSelectedWishLists(
    {
      itemId,
      type,
      authorization,
    }: {
      itemId: number;
      type: any;
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/wishlists/selected`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (WishList Items)
   * Get wish list items by wish list id
   * @returns WishListItemsPaginatedViewModel Success
   * @throws ApiError
   */
  public static async getWishListItems(
    {
      wishListId,
      page,
      size,
    }: {
      wishListId: string;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WishListItemsPaginatedViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WishListItemsPaginatedViewModel>({
      method: 'GET',
      url: `/v2/wishlists/items/${wishListId}`,
      path: {
        ...(wishListId ? { wishListId: wishListId } : {}),
      },
      query: {
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Add (WishList Item)
   * Adds new wishlist item
   * @returns AddWishListItemViewModel Success
   * @throws ApiError
   */
  public static async addWishList(
    {
      wishListId,
      authorization,
      body,
    }: {
      wishListId: string;
      authorization: string;
      body?: AddWishListItemRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<AddWishListItemViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<AddWishListItemViewModel>({
      method: 'POST',
      url: `/v2/wishlists/items/${wishListId}/add`,
      path: {
        ...(wishListId ? { wishListId: wishListId } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Delete (WishList Item)
   * Deletes wishlist item by id
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async deleteWishListItem(
    {
      wishListItemId,
    }: {
      wishListItemId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/wishlists/items/${wishListItemId}/delete`,
      path: {
        ...(wishListItemId ? { wishListItemId: wishListItemId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    WishListsService: typeof WishListsService;
  }
}
